import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Check if token is expired
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          logout();
          return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`${API_URL}/current-user`);
        setUser(response.data);
      } catch (error) {
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/login`, { username, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    return userData;
  };

  const signup = async (userData) => {
    await axios.post(`${API_URL}/signup`, userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updatePassword = async (oldPassword, newPassword) => {
    await axios.put(`${API_URL}/update-password`, {
      old_password: oldPassword,
      new_password: newPassword
    });
  };

  const hasPermission = (module, action) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.permissions?.[module]?.[action] === true;
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.permissions?.[module]?.view === true;
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    updatePassword,
    hasPermission,
    hasModuleAccess,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};